import { createSlice } from '@reduxjs/toolkit';
import { BASE_END_POINT_COMPANY } from '../../../config';
import { getLocalStorageAuthentication } from '../../../lib/auth';
import { messageUpdated } from '../app/appSlice';
import { Severity } from '../../../components/Message/Message';

const initialState = {
  bundleStatus: {
    hasActiveBundle: false,
    planName: null,
    totalCredits: 0,
    remainingCredits: 0,
    createdAt: null
  },
  bundleHistory: [],
  loading: false,
  historyLoading: false,
  error: null,
  historyError: null,
  lastFetched: null,
  historyLastFetched: null
};

const bundleSlice = createSlice({
  name: 'bundle',
  initialState,
  reducers: {
    // Loading state
    fetchBundleStatusStarted: (state) => {
      state.loading = true;
      state.error = null;
    },
    // Success state
    fetchBundleStatusSucceeded: (state, action) => {
      state.bundleStatus = action.payload;
      state.loading = false;
      state.error = null;
      state.lastFetched = new Date().toISOString();
    },
    // Error state
    fetchBundleStatusFailed: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Bundle history loading state
    fetchBundleHistoryStarted: (state) => {
      state.historyLoading = true;
      state.historyError = null;
    },
    fetchBundleHistorySucceeded: (state, action) => {
      state.bundleHistory = action.payload;
      state.historyLoading = false;
      state.historyError = null;
      state.historyLastFetched = new Date().toISOString();
    },
    fetchBundleHistoryFailed: (state, action) => {
      state.historyLoading = false;
      state.historyError = action.payload;
    },
    // Update bundle status after credit consumption
    bundleStatusUpdated: (state, action) => {
      state.bundleStatus = {
        ...state.bundleStatus,
        ...action.payload
      };
    },
    // Optimistically decrement credits
    creditConsumed: (state) => {
      if (state.bundleStatus.remainingCredits > 0) {
        state.bundleStatus.remainingCredits -= 1;
      }
    },
    // Reset bundle state
    bundleStatusReset: (state) => {
      state.bundleStatus = initialState.bundleStatus;
      state.bundleHistory = initialState.bundleHistory;
      state.loading = false;
      state.historyLoading = false;
      state.error = null;
      state.historyError = null;
      state.lastFetched = null;
      state.historyLastFetched = null;
    }
  }
});

export const {
  fetchBundleStatusStarted,
  fetchBundleStatusSucceeded,
  fetchBundleStatusFailed,
  fetchBundleHistoryStarted,
  fetchBundleHistorySucceeded,
  fetchBundleHistoryFailed,
  bundleStatusUpdated,
  creditConsumed,
  bundleStatusReset
} = bundleSlice.actions;

// Selectors
export const bundleStatusSelector = (state) => state.bundle.bundleStatus;
export const bundleHistorySelector = (state) => state.bundle.bundleHistory;
export const bundleLoadingSelector = (state) => state.bundle.loading;
export const bundleHistoryLoadingSelector = (state) =>
  state.bundle.historyLoading;
export const bundleErrorSelector = (state) => state.bundle.error;
export const bundleHistoryErrorSelector = (state) => state.bundle.historyError;
export const hasCreditsSelector = (state) =>
  state.bundle.bundleStatus.hasActiveBundle &&
  state.bundle.bundleStatus.remainingCredits > 0;
export const remainingCreditsSelector = (state) =>
  state.bundle.bundleStatus.remainingCredits;
export const bundleLastFetchedSelector = (state) => state.bundle.lastFetched;
export const bundleHistoryLastFetchedSelector = (state) =>
  state.bundle.historyLastFetched;

// Thunk: Fetch bundle status from API
export const fetchBundleStatus = () => async (dispatch) => {
  try {
    dispatch(fetchBundleStatusStarted());

    const localAuth = getLocalStorageAuthentication();
    if (!localAuth || !localAuth.token) {
      console.warn('⚠️ [BUNDLE] No auth token found, skipping bundle fetch');
      dispatch(
        fetchBundleStatusSucceeded({
          hasActiveBundle: false,
          planName: null,
          totalCredits: 0,
          remainingCredits: 0,
          createdAt: null
        })
      );
      return;
    }

    const url = `${BASE_END_POINT_COMPANY}/api/v1/job-bundle/status`;
    console.log('🔄 [BUNDLE] Fetching bundle status from:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localAuth.token}`
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(
        '❌ [BUNDLE] Failed to fetch bundle status:',
        response.status,
        errorText
      );
      throw new Error(
        `Failed to fetch bundle status: ${response.status} ${errorText}`
      );
    }

    const responseData = await response.json();
    console.log('✅ [BUNDLE] Bundle status response:', responseData);

    // Extract data from ResponseAPI wrapper
    const bundleData = responseData.data || responseData;

    dispatch(fetchBundleStatusSucceeded(bundleData));

    // Log bundle status for debugging
    if (bundleData.hasActiveBundle) {
      console.log(
        `💳 [BUNDLE] Active bundle found: ${bundleData.planName} | ${bundleData.remainingCredits}/${bundleData.totalCredits} credits remaining`
      );
    } else {
      console.log('ℹ️ [BUNDLE] No active bundle found');
    }
  } catch (error) {
    console.error('❌ [BUNDLE] Error fetching bundle status:', error);
    dispatch(fetchBundleStatusFailed(error.message));
    dispatch(
      messageUpdated({
        text: 'Failed to load bundle status. Please refresh the page.',
        severity: Severity.ERROR
      })
    );
  }
};

// Thunk: Fetch bundle history from API
export const fetchBundleHistory = () => async (dispatch) => {
  try {
    dispatch(fetchBundleHistoryStarted());

    const localAuth = getLocalStorageAuthentication();
    if (!localAuth || !localAuth.token) {
      console.warn(
        '⚠️ [BUNDLE] No auth token found, skipping bundle history fetch'
      );
      dispatch(fetchBundleHistorySucceeded([]));
      return;
    }

    const url = `${BASE_END_POINT_COMPANY}/api/v1/job-bundle/history`;
    console.log('🔄 [BUNDLE] Fetching bundle history from:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localAuth.token}`
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(
        '❌ [BUNDLE] Failed to fetch bundle history:',
        response.status,
        errorText
      );
      throw new Error(
        `Failed to fetch bundle history: ${response.status} ${errorText}`
      );
    }

    const responseData = await response.json();
    console.log('✅ [BUNDLE] Bundle history response:', responseData);

    const historyData = responseData.data || responseData;
    dispatch(fetchBundleHistorySucceeded(historyData || []));
  } catch (error) {
    console.error('❌ [BUNDLE] Error fetching bundle history:', error);
    dispatch(fetchBundleHistoryFailed(error.message));
    dispatch(
      messageUpdated({
        text: 'Failed to load bundle history. Please refresh the page.',
        severity: Severity.ERROR
      })
    );
  }
};

// Thunk: Refresh bundle status (force refetch)
export const refreshBundleStatus = () => async (dispatch) => {
  console.log('🔄 [BUNDLE] Refreshing bundle status...');
  dispatch(fetchBundleStatus());
};

export default bundleSlice.reducer;
